import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Button, Box, CircularProgress, IconButton, Divider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

import { usePreAnaestheticForm } from './hooks/usePreAnaestheticForm';
import SignatureDialog from './components/SignatureDialog';

import BasicInfoSection from './screens/BasicInfoSection';
import MedicalIllnessSection from './screens/MedicalIllnessSection';
import HistoryAndAirwaySection from './screens/HistoryAndAirwaySection';
import VitalsAndExamsSection from './screens/VitalsAndExamsSection';
import InvestigationsSection from './screens/InvestigationsSection';
import AnaesthesiaPlanSection from './screens/AnaesthesiaPlanSection';
import SignaturesSection from './screens/SignaturesSection';

const PreAnaestheticForm = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    errors,
    loading,
    isInitialLoading,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    isUpdate,
    nextClicked,
    setNextClicked,
    handleChange,
    handleNameChange,
    handleBlur,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare,
    handleNextClick,
  } = usePreAnaestheticForm(uhid_no, navigate);

  const handleBack = () => {
    if (nextClicked) {
      setNextClicked(false);
    } else {
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    }
  };

  const goToVideoRecorder = () => {
    navigate(`/video?form=${encodeURIComponent("PRE-ANAESTHETIC EVALUATION")}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  if (isInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Button
                onClick={handleBack}
                startIcon={<span>←</span>}
                sx={{ color: '#008080', fontWeight: 600, mb: 1, textTransform: 'none' }}
              >
                Back
              </Button>
              <Typography
                variant="h4"
                sx={{ color: '#008080', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                PRE-ANAESTHETIC EVALUATION
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: '32px', height: '32px' }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 600 }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Action Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleHomeNavigation} sx={{ bgcolor: '#008080', textTransform: 'none' }}>
                Home
              </Button>
              <Button variant="outlined" onClick={goToVideoRecorder} sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none' }}>
                Add Iterative Video
              </Button>
            </Box>
            <IconButton color="primary" onClick={handleShare} sx={{ border: '1px solid #008080' }}>
              <ShareIcon />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            {!nextClicked ? (
              <>
                <BasicInfoSection 
                  formData={formData} 
                  handleChange={handleChange} 
                  handleNameChange={handleNameChange}
                  setFormData={setFormData}
                  errors={errors} 
                  handleBlur={handleBlur}
                />
                <Divider sx={{ my: 4 }} />

                <MedicalIllnessSection formData={formData} handleChange={handleChange} setFormData={setFormData} />
                <Divider sx={{ my: 4 }} />

                <HistoryAndAirwaySection formData={formData} handleChange={handleChange} setFormData={setFormData} />
                <Divider sx={{ my: 4 }} />

                <VitalsAndExamsSection formData={formData} handleChange={handleChange} setFormData={setFormData} />

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                  <DraftSaveButton
                    formData={formData}
                    formKey={DRAFT_KEYS.PRE_ANAESTHETIC_FORM}
                    uhidNo={uhid_no}
                    isUpdate={isUpdate}
                    onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  />
                  <Button variant="contained" color="primary" fullWidth onClick={handleNextClick} sx={{ bgcolor: '#008080' }}>
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <InvestigationsSection formData={formData} handleChange={handleChange} setFormData={setFormData} />
                <AnaesthesiaPlanSection formData={formData} handleChange={handleChange} setFormData={setFormData} />
                <SignaturesSection 
                  formData={formData} 
                  handleSignatureOpen={handleSignatureOpen}
                  handleNameChange={handleNameChange}
                  handleBlur={handleBlur}
                  errors={errors}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setNextClicked(false)}
                    sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none', minWidth: 100 }}
                  >
                    ← Back
                  </Button>
                  {!isUpdate && (
                    <DraftSaveButton
                      formData={formData}
                      formKey={DRAFT_KEYS.PRE_ANAESTHETIC_FORM}
                      uhidNo={uhid_no}
                      isUpdate={false}
                      onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                    />
                  )}
                  <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ bgcolor: '#008080' }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : (isUpdate ? 'Update' : 'Submit')}
                  </Button>
                </Box>
              </>
            )}
          </form>

          {/* Signature Dialog */}
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PreAnaestheticForm;
